<script lang="ts">
	import UsersGroup from '$lib/assets/images/icons/shield-check.svg?component';
	import ContactFormVerification from '$lib/components/Global/ContactFormVerification.svelte';
	import Step from '$lib/components/Simulations/Components/Stepper/Step.svelte';
	import __ from '$lib/stores/TranslationStore';
	import { convertToCurrency } from '$lib/utils/helpers/numbers';
	import { tick } from 'svelte';
	import ChoiceField from '../GenericSimulation/Fields/ChoiceField.svelte';
	import DateInput, { formatStandardDate } from '../GenericSimulation/Fields/DateInput.svelte';
	import Input from '../GenericSimulation/Fields/Input.svelte';
	import MoneyInput from '../GenericSimulation/Fields/MoneyInput.svelte';
	import GenericSimulation from '../GenericSimulation/GenericSimulation.svelte';
	import ContactSubmitted from '../GenericSimulation/Result/ContactSubmitted.svelte';
	import MinInstallmentCard from '../GenericSimulation/Result/Insurance/MinInstallmentCard.svelte';
	import Partnerships from '../GenericSimulation/Result/Partnerships.svelte';
	import ResultFrame from '../GenericSimulation/Result/ResultFrame.svelte';
	import TaxCard from '../GenericSimulation/Result/TaxCard.svelte';
	import ContactStep from '../GenericSimulation/Steps/ContactStep.svelte';
	import InsuranceProblem from '../Result/InsuranceProblem.svelte';
	import { type StepperStore } from '../Stepper/stepper';
	import Stepper from '../Stepper/Stepper.svelte';

	let result: Awaited<ReturnType<typeof submitSimulation>> | null = null;
	let postResult: {
		name: string;
		product: string;
		simulation: {
			monthly_installment: number | null;
			quarterly_installment: number | null;
			semi_annual_installment: number | null;
			annual_installment: number;
		};
	} | null = null;

	let resultVerified = false;
	let submitted = false;
	let error = false;

	let contactSubmitted = false;
	let contactDialogOpen = false;

	async function submitSimulation(data: Record<string, unknown>) {
		const response = await fetch('/api/crm/simulations/home-life-insurance', {
			method: 'POST',
			body: JSON.stringify({
				capital: convertToCurrency(data.insurance_amount as string),
				start_date: formatStandardDate(data.initial_date as string),
				recieve_communications: data.recieve_communications,
				main_holder: {
					name: data.name,
					email: data.email,
					birthdate: formatStandardDate(data.birthdate as string),
					phone: data.phone,
					prefix: '+351'
				},
				second_holder:
					data.two_holders === 'yes'
						? {
								name: data.name_two,
								birthdate: formatStandardDate((data.birthdate_two as string) || '')
						  }
						: {}
			})
		});
		const json = await response.json();

		if (!response.ok || !json.success) {
			throw new Error('Error submitting simulation');
		}

		if (window?.gtag) window?.gtag('event', 'Submit', { event_category: 'Sim Seguro Vida Hab' });

		return json?.data?.data as {
			lead: string;
			s_id: string;
			name: string;
			simulation: {
				monthly_installment: number | null;
				quarterly_installment: number | null;
				semi_annual_installment: number | null;
				annual_installment: number;
			};
		};
	}

	async function handleResults(stepper: StepperStore) {
		if (submitted) return;
		submitted = true;
		simulator.doLoading(async () => {
			result = await submitSimulation(stepper.extractAllData());
            resultVerified = true;

			postResult = result as typeof postResult;
			stepper.showResult();
		});
	}

	async function handleDirectContact(stepper: StepperStore) {
		try {
			const response = await fetch('/api/crm/leads/' + result?.lead + '/direct-contact', {
				method: 'POST'
			});

			const json = await response.json();
			contactDialogOpen = true;
			contactSubmitted = true;
		} catch (error) {
			console.log(error);
		}
	}

	let twoHolders: ChoiceField;
	let hasTwoHolders = false;

	function handleRetry(stepper: StepperStore) {
		stepper.reset();
		result = null;
		postResult = null;
		resultVerified = false;
		submitted = false;
		contactSubmitted = false;
		contactDialogOpen = false;
		error = false;
	}

	function handleSmsVerificationSuccess(stepper: StepperStore) {
		simulator.doLoading(async () => {
			// let res = await fetch('/api/crm/simulations/' + result?.s_id);
			// const json = await res.json();

			// if (!res.ok || !json.success) {
			// 	throw new Error('Error submitting simulation');
			// }

			postResult = result as typeof postResult;
			resultVerified = true;
		});
	}

	function initialDateValidation(value: unknown) {
		if (typeof value != 'string' || value.split('/').length < 2) {
			return $__('A data de início deve estar no formato dd/mm/aaaa');
		}

		const dateParts = value.split('/');
		const inputDate = new Date(+dateParts[2], parseInt(dateParts[1]) - 1, +dateParts[0]);

		if (isNaN(inputDate.getTime())) {
			return $__('A data de início deve estar no formato dd/mm/aaaa');
		}

		const now = new Date();
		now.setHours(0, 0, 0, 0);

		const twoMonthsLater = new Date();
		twoMonthsLater.setMonth(now.getMonth() + 2);

		if (inputDate < now || inputDate >= twoMonthsLater) {
			return $__('A data de início deve estar entre a data atual e 2 meses depois');
		}
	}

	let simulator: GenericSimulation;
</script>

<Stepper let:stepper>
	<GenericSimulation
		title={$__('Simulador')}
		caption={$__('Seguro Vida Habitação')}
		icon={UsersGroup}
		bind:this={simulator}
		on:retry={() => handleRetry(stepper)}
	>
		<!-- {@const test = stepper.showResult()} -->
		<Step
			title={$__('<strong>Quando</strong> pretende começar a estar seguro?')}
			required={['initial_date']}
			displayCaption={(step) => step.data.initial_date + ''}
			validate={{
				initial_date: (value) => initialDateValidation(value)
			}}
		>
			<DateInput name="initial_date" label={$__('Data de início')} />
		</Step>
		<Step
			title={$__('Qual o <strong>valor</strong> do <strong>crédito contratado</strong>?')}
			validate={{
				insurance_amount: (value) => {
					const num = convertToCurrency(value);

					if (num > 765_000) {
						return $__('O valor a segurar deve ser menor do que 765.000 €');
					}
				}
			}}
			required={['insurance_amount']}
			displayCaption={(step) => `${step.data.insurance_amount} €`}
		>
			<MoneyInput name="insurance_amount" focusOnMount label={$__('Valor solicitado')} />
		</Step>
		<ContactStep exceptFields={['nif']} />
		<Step
			title={$__('É uma proposta para <strong>segurar duas pessoas</strong>?')}
			actionButton={false}
		>
			<ChoiceField
				name="two_holders"
				bind:this={twoHolders}
				variant="grid-only"
				on:change={(e) => {
					hasTwoHolders = e.detail === 'yes';
					if (hasTwoHolders) {
						twoHolders.safeChange(async () => {
							await tick();
							stepper.goNextStep();
						});
						return;
					}
					handleResults(stepper);
				}}
				options={[
					{ label: $__('Sim'), value: 'yes' },
					{ label: $__('Não'), value: 'no' }
				]}
			/>
		</Step>
		{#if hasTwoHolders}
			<Step
				title={$__('Dados pessoais <strong>da 2.ª pessoa segurada</strong>')}
				on:complete={() => handleResults(stepper)}
				displayCaption={(step) => `
					${step.data.name_two}
					${step.data.birthdate_two}
				`}
				validate={{
					name_two: (value) => {
						if (!/^(?=.{2,})([A-Za-zÀ-ÖØ-öø-ÿ\-]+(?:\s[A-Za-zÀ-ÖØ-öø-ÿ\-]+)+)$/.test(value)) {
							return $__('Deve colocar, no mínimo, dois nomes.');
						}
					}
				}}
				required={['name_two', 'birthdate_two']}
			>
				<div class="flex flex-col gap-6">
					<Input name="name_two" label={$__('Nome e apelido')} />
					<DateInput name="birthdate_two" label={$__('Data de nascimento')} />
				</div>
			</Step>
		{/if}
		<div slot="content" class="flex flex-col justify-center">
			{#if error}
				<p>Error</p>
				<!-- <div class="-mt-4 h-full w-full">
					<SimulationError on:retry={() => handleRetry(stepper)} />
				</div> -->
			{/if}
			{#if result}
				{#if resultVerified && postResult}
					{#if contactDialogOpen}
						<ContactSubmitted on:close={() => (contactDialogOpen = false)} />
					{/if}
					<ResultFrame
						clientName={postResult.name}
						on:retry={() => handleRetry(stepper)}
						directContactRequested={contactSubmitted}
						on:contact={() => handleDirectContact(stepper)}
						custom={postResult.simulation.annual_installment == null}
					>
						<div slot="custom">
							<InsuranceProblem name={postResult.name} />
						</div>
						<div
							class="grid grid-cols-1 grid-rows-2 items-start justify-center gap-3 md:grid-cols-2 md:grid-rows-1 md:flex-row md:gap-4 lg:gap-6"
						>
							<MinInstallmentCard
								class="ml-auto w-full md:w-auto"
								installmentValues={{
									monthly: postResult.simulation.monthly_installment,
									quarterly: postResult.simulation.quarterly_installment,
									semi_annual: postResult.simulation.semi_annual_installment
								}}
							/>
							<TaxCard
								class="mr-auto w-full md:w-auto"
								baseColor="secondary"
								bottomText={$__('Anual')}
								amount={postResult.simulation.annual_installment}
								title={$__('Seguro vida')}
								caption={$__('Habitação')}
							/>
						</div>
						<svelte:fragment slot="note">
							{$__(
								'A simulação de seguro apresentada tem como base a cobertura de Invalidez Absoluta e Definitiva. O prazo máximo de financiamento foi calculado com base na idade indicada. As condições podem variar consoante o banco. Entre em contacto para ter mais informações e avançar com o processo online gratuito.'
							)}
						</svelte:fragment>
						<Partnerships
							insurance
							slot="bottom"
							caption={$__('Intermediação com as principais seguradoras do país.')}
						/>
					</ResultFrame>
				{:else}
					<div class=" self-center">
						<ContactFormVerification
							on:success={() => handleSmsVerificationSuccess(stepper)}
							verificationId={result?.lead}
						/>
					</div>
				{/if}
			{/if}
		</div>
	</GenericSimulation>
</Stepper>
